import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';

import { FiscalYearsRes, FiscalYear, ReportId, SelectOption } from 'models';
import { BalanceType } from 'utils/enums';
import { toTimezoneDate } from 'utils/date-timezone';
import { balanceTypesOptions } from 'configs/balances-report';

import { HTMLParser, PageTitle } from 'containers';
import {
  FiltersBar,
  Select,
  DatePicker,
  Download,
  NoData,
  Error,
  ControlWrapper,
} from 'components';
import Box from '@material-ui/core/Box';
import PageLoader from 'components/page-loader';

import clsx from 'clsx';
import { useStyles } from './style';

type Version = {
  label: string;
  value: number;
};

interface Props {
  fileName?: string;
  minDate?: Date | null;
  maxDate?: Date | null;
  fiscalYearsData: FiscalYearsRes | null;
  selectedFiscalYears: FiscalYear | null;
  startDate: Date | null;
  endDate: Date | null;
  loading: boolean;
  error: boolean;
  version: Version;
  iframeURL: string | null;
  reportId?: ReportId;
  balanceType: SelectOption<BalanceType> | null;
  onChangeVersion: (version: Version) => void;
  onChangeFiscalYear: (fiscalYear: FiscalYear) => void;
  onChangeStartDate: (date: Date | null) => void;
  onChangeEndDate: (date: Date | null) => void;
  onChangeBalanceType: (option: SelectOption<BalanceType>) => void;
}

const CooperativeBudgetDetails: React.FC<Props> = ({
  balanceType,
  fiscalYearsData,
  loading,
  error,
  startDate,
  endDate,
  selectedFiscalYears,
  onChangeFiscalYear,
  onChangeStartDate,
  onChangeEndDate,
  onChangeBalanceType,
  version,
  reportId,
  onChangeVersion,
  iframeURL,
  minDate,
  maxDate,
  fileName,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const selectCls = useMemo(
    () => ({
      value: classes.value,
      label: classes.label,
      btn: {
        root: classes.btn,
      },
    }),
    [classes]
  );

  const versions: Version[] = useMemo(() => {
    return [
      {
        label: t('#portal.report.bar.option.label.VersionSelect.Short'),
        value: 0,
      },
      {
        label: t('#portal.report.bar.option.label.VersionSelect.Long'),
        value: 1,
      },
    ];
  }, [t]);

  const isCooperativeBudgetReport = reportId === 'cooperative-budget';

  if (error) return <Error />;
  if (loading) return <PageLoader />;
  if (
    !fiscalYearsData ||
    (isCooperativeBudgetReport && !fiscalYearsData.ClosedPeriodEnd)
  )
    return <NoData />;

  return (
    <>
      <PageTitle />
      <FiltersBar className={classes.column}>
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          justifyContent="space-between"
          width="100%"
        >
          <Box className={classes.barWrapper}>
            <ControlWrapper className={classes.controlWrapper}>
              <span className={classes.label}>
                {`${t('#portal.report.bar.label.fiscalyear')}:`}
              </span>
              <Select
                value={selectedFiscalYears}
                options={fiscalYearsData.FiscalYears}
                onSelect={onChangeFiscalYear}
                labelField="Name"
                valueField="Id"
                withButton
                cls={selectCls}
              />
            </ControlWrapper>
            <ControlWrapper className={classes.controlWrapper}>
              <span className={classes.label}>
                {`${t('#portal.report.bar.label.VersionSelect')}:`}
              </span>
              <Select
                value={version}
                options={versions}
                onSelect={onChangeVersion}
                labelField="label"
                valueField="value"
                withButton
                cls={selectCls}
              />
            </ControlWrapper>
            {!isCooperativeBudgetReport && (
              <ControlWrapper>
                <span className={classes.label}>Näytä:</span>
                <Select
                  value={balanceType}
                  options={balanceTypesOptions}
                  onSelect={onChangeBalanceType}
                  labelField="label"
                  valueField="value"
                  withButton
                  cls={selectCls}
                />
              </ControlWrapper>
            )}
          </Box>

          {fiscalYearsData.ClosedPeriodEnd && isCooperativeBudgetReport ? (
            <Box padding="10px">
              <span className={clsx(classes.label, classes.m0)}>
                Kirjanpito tarkistettu{' '}
                {format(
                  toTimezoneDate(fiscalYearsData.ClosedPeriodEnd),
                  'd.M.yyyy'
                )}
                {' asti'}
              </span>
            </Box>
          ) : null}
        </Box>
        <div className={classes.barWrapper}>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {`${t('#portal.report.bar.label.startdate')}:`}
            </span>
            <DatePicker
              date={startDate}
              handleChange={onChangeStartDate}
              minDate={minDate}
              maxDate={endDate}
            />
          </ControlWrapper>
          <ControlWrapper className={classes.controlWrapper}>
            <span className={classes.label}>
              {`${t('#portal.report.bar.label.enddate')}:`}
            </span>
            <DatePicker
              date={endDate}
              handleChange={onChangeEndDate}
              minDate={startDate}
              maxDate={maxDate}
            />
          </ControlWrapper>
          <ControlWrapper className={classes.btnControl}>
            <Download name={fileName} url={`${iframeURL}&download=true`} />
          </ControlWrapper>
        </div>
      </FiltersBar>
      {iframeURL ? (
        <HTMLParser
          url={iframeURL}
          reportId={reportId}
          fiscalYearId={selectedFiscalYears?.Id}
          startDate={
            startDate
              ? format(startDate, "yyyy-MM-dd'T'00:00:00.000")
              : undefined
          }
          endDate={
            endDate ? format(endDate, "yyyy-MM-dd'T'00:00:00.000") : undefined
          }
        />
      ) : null}
    </>
  );
};

export default CooperativeBudgetDetails;
